import type { Database } from '@/database.types';
import type { PlannedGoal } from '@/entities/planned-goal';
import { plannedGoalSchema } from '@/entities/planned-goal/schema';

function toPlannedGoal(
  data: Database['public']['Tables']['planned_goals']['Row'],
): PlannedGoal {
  return plannedGoalSchema.parse({
    id: data.id,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    goalId: data.goal_id,
    planId: data.plan_id,
    sortKey: data.sort_key,
  });
}

export function createPlannedGoalsService(
  supabaseClient: typeof import('./supabase').supabaseClient,
) {
  return {
    readPlannedGoalsByPlanId: async (
      planId: string,
    ): Promise<PlannedGoal[]> => {
      const { data, error } = await supabaseClient
        .from('planned_goals')
        .select('*')
        .eq('plan_id', planId)
        .order('sort_key');

      if (error) {
        throw error;
      }

      return data.map(toPlannedGoal);
    },

    addGoalsToPlan: async (
      items: Array<{
        id: string;
        goalId: string;
        planId: string;
        sortKey: string;
      }>,
    ): Promise<PlannedGoal[]> => {
      const payload = items.map((item) => ({
        id: item.id,
        goal_id: item.goalId,
        plan_id: item.planId,
        sort_key: item.sortKey,
      }));

      const { data, error } = await supabaseClient
        .from('planned_goals')
        .insert(payload)
        .select();

      if (error) {
        throw error;
      }

      return data.map(toPlannedGoal);
    },

    removeGoalFromPlan: async (goalId: string): Promise<void> => {
      const { error } = await supabaseClient
        .from('goals')
        .delete()
        .eq('id', goalId);

      if (error) {
        throw error;
      }
    },

    reorderGoalInPlan: async (
      plannedGoalId: string,
      sortKey: string,
    ): Promise<PlannedGoal> => {
      const { data, error } = await supabaseClient
        .from('planned_goals')
        .update({ sort_key: sortKey })
        .eq('id', plannedGoalId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return toPlannedGoal(data);
    },
  };
}
